import { type MaybeRefOrGetter, ref, toValue } from "vue";
import { useToast } from "primevue/usetoast";

export interface TeamOption {
  id: number;
  name: string;
}

interface UseOrganizationTeamsOptions {
  /**
   * Whether to show toast errors
   */
  showErrors?: boolean;
}

/**
 * Composable for fetching teams from an organization.
 * Consolidates the common pattern of loading teams. `orgId` may be a ref or
 * getter; it is read on every `loadTeams()` call.
 */
export function useOrganizationTeams(
  orgId: MaybeRefOrGetter<number | null | undefined>,
  options: UseOrganizationTeamsOptions = {}
) {
  const { showErrors = true } = options;
  const toast = useToast();

  const teams = ref<TeamOption[]>([]);
  const isLoading = ref(false);
  const error = ref<Error | null>(null);

  /**
   * Load teams from an organization
   */
  async function loadTeams() {
    const currentOrgId = toValue(orgId);
    // Don't load if we don't have a valid org ID
    if (!currentOrgId) {
      teams.value = [];
      return false;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const { getTeams } = await import("@/modules/teams/api");
      const response = await getTeams(1000, 0, currentOrgId);

      if (!("error" in response)) {
        teams.value = (response.results || []).map((t) => ({
          id: t.id,
          name: t.name || "",
        }));

        return true;
      }

      return false;
    } catch (err) {
      console.error("Error loading teams:", err);
      error.value = err as Error;

      if (showErrors) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to load teams. Please try again.",
          life: 5000,
        });
      }

      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Reset teams to empty array
   */
  function reset() {
    teams.value = [];
    error.value = null;
  }

  return {
    teams,
    isLoading,
    error,
    loadTeams,
    reset,
  };
}
